import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "./ui/use-toast";

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    whatsapp: "",
    package: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.whatsapp || !formData.package) {
      toast({
        title: "Mohon lengkapi data",
        description: "Nama, WhatsApp, dan Paket wajib diisi.",
        variant: "destructive",
      });
      return;
    }

    const message = encodeURIComponent(
      `Halo Admin Toyibah Aqiqah 👋,\n\nSaya ingin memesan layanan aqiqah:\nNama: ${formData.name}\nNo. WhatsApp: ${formData.whatsapp}\nPaket: ${formData.package}\nPesan: ${formData.message || "-"}\n\nMohon bantu proses selanjutnya. Terima kasih 🙏`
    );

    window.open(`https://wa.me/6281295138575?text=${message}`, "_blank");

    toast({
      title: "Mengarahkan ke WhatsApp...",
      description: "Silakan lanjutkan percakapan dengan admin kami.",
      duration: 3000,
    });

    setFormData({
      name: "",
      whatsapp: "",
      package: "",
      message: "",
    });
  };

  return (
    <section
      id="contact"
      className="relative py-16 md:py-20 px-6 bg-gradient-to-b from-[#FFF9F7] via-[#FFF5F2] to-[#FFFFFF] overflow-hidden pt-28 scroll-mt-20"
    >
      {/* ✨ Background Pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 1L12 23M1 12L23 12' stroke='%23FF8A65' stroke-width='0.4'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-2xl md:text-4xl font-extrabold text-[#333] mb-4"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Siap Mengaqiqahkan{" "}
          <span className="text-[#FF8A65]">Buah Hati Anda?</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-[#777] text-sm md:text-base mb-10 max-w-2xl mx-auto leading-relaxed"
        >
          Isi data di bawah ini untuk langsung terhubung dengan admin kami via
          WhatsApp. Proses mudah, cepat, dan amanah.
        </motion.p>

        {/* 💬 Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white/90 backdrop-blur-sm border border-[#FFD8C8]/50 rounded-2xl shadow-[0_6px_20px_rgba(0,0,0,0.05)] p-6 md:p-8 text-left max-w-md mx-auto"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              id="name"
              placeholder="Nama Lengkap"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="h-11 text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all"
            />

            <Input
              id="whatsapp"
              type="tel"
              placeholder="Nomor WhatsApp"
              value={formData.whatsapp}
              onChange={(e) =>
                setFormData({ ...formData, whatsapp: e.target.value })
              }
              className="h-11 text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all"
            />

            {/* Dropdown Paket */}
            <Select
              value={formData.package}
              onValueChange={(value) =>
                setFormData({ ...formData, package: value })
              }
            >
              <SelectTrigger className="h-11 text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all">
                <SelectValue placeholder="Pilih Paket Aqiqah" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Paket A (12–13kg, Sate 200 / Gule 40)">
                  PAKET A — 12–13 kg | Sate 200 / Gule 40
                </SelectItem>
                <SelectItem value="Paket B (14–15kg, Sate 250 / Gule 50)">
                  PAKET B — 14–15 kg | Sate 250 / Gule 50
                </SelectItem>
                <SelectItem value="Paket C (15–16kg, Sate 300 / Gule 60)">
                  PAKET C — 15–16 kg | Sate 300 / Gule 60
                </SelectItem>
                <SelectItem value="Paket D (17–18kg, Sate 350 / Gule 70)">
                  PAKET D — 17–18 kg | Sate 350 / Gule 70
                </SelectItem>
                <SelectItem value="Super (19–20kg, Sate 400 / Gule 80)">
                  SUPER — 19–20 kg | Sate 400 / Gule 80
                </SelectItem>
                <SelectItem value="Super+ (20–21kg, Sate 450 / Gule 90)">
                  SUPER+ — 20–21 kg | Sate 450 / Gule 90
                </SelectItem>
                <SelectItem value="Istimewa (22–23kg, Sate 500 / Gule 100)">
                  ISTIMEWA — 22–23 kg | Sate 500 / Gule 100
                </SelectItem>
                <SelectItem value="Kotakan (80 Kotak Nasi Lengkap)">
                  KOTAKAN — 80 Kotak Nasi Lengkap
                </SelectItem>
                <SelectItem value="Konsultasi Dulu">Konsultasi Dulu</SelectItem>
              </SelectContent>
            </Select>

            <Textarea
              id="message"
              placeholder="Pesan tambahan (opsional)..."
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              className="text-sm md:text-base bg-gray-50 focus:bg-white border-[#FFE3D8] focus:border-[#FF8A65] transition-all min-h-[90px]"
            />

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white py-5 rounded-xl font-bold text-base md:text-lg shadow-lg transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
            >
              <Send className="h-4 w-4 md:h-5 md:w-5" />
              Chat via WhatsApp
            </Button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
